import { useEffect } from "react";

/**
 * Attaches an IntersectionObserver to all elements matching `selector`
 * inside `rootRef?.current` (or document by default). When an element
 * enters the viewport it gets the `is-visible` class added exactly once,
 * triggering the CSS transition defined on `.reveal-on-scroll`.
 */
export function useRevealOnScroll(selector = ".reveal-on-scroll", options) {
  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;

    const nodes = Array.from(document.querySelectorAll(selector));
    if (!nodes.length) return;

    // If user prefers reduced motion, just reveal immediately
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      nodes.forEach((n) => n.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px", ...options }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [selector, options]);
}
